// Tank level from a level-transmitter signal — mirror of backend/tank_level.py (the source of
// truth; /assets/live-values returns its result as `live.tank`). Used here for what the backend
// doesn't compute per point: trend charts built from raw history, and as a fallback if a live
// value arrives without `tank`. Keep the two in step; backend/tests/test_tank_level.py holds the
// shared cases (client spec T1–T9 + the calculator's Sample Tests).

// mode → [signal min, signal max, fault low, fault high, unit]. Fault limits: min − 2.5 % of span,
// max + 6.25 % of span (NAMUR NE43 ratios → 3.6 / 21 mA on 4–20 mA).
export const SIGNAL_MODES = {
  "4-20mA": [4, 20, 3.6, 21, "mA"],
  "0-10V": [0, 10, -0.25, 10.625, "V"],
  "0-5V": [0, 5, -0.125, 5.3125, "V"],
  "1-5V": [1, 5, 0.9, 5.25, "V"],
  "2-10V": [2, 10, 1.8, 10.5, "V"],
  custom: [4, 20, 3.6, 21, "mA"],
};
export const SIGNAL_MODE_OPTIONS = [
  { v: "4-20mA", label: "4–20 mA" },
  { v: "0-10V", label: "0–10 V" },
  { v: "0-5V", label: "0–5 V" },
  { v: "1-5V", label: "1–5 V (live zero)" },
  { v: "2-10V", label: "2–10 V (live zero)" },
  { v: "custom", label: "Custom (set endpoints below)" },
];
const DEFAULT_LOW_PCT = 25;
const DEFAULT_VERY_LOW_PCT = 10;
export const VALID_STATUSES = new Set(["OK", "UNDER-RANGE", "OVER-RANGE"]);

export function normalizeMode(raw) {
  const s = String(raw ?? "").trim().toLowerCase().replace(/–/g, "-").replace(/\s+/g, "");
  if (s === "" || s === "4-20ma") return "4-20mA";
  return Object.keys(SIGNAL_MODES).find((k) => k.toLowerCase() === s) || null;
}

const num = (v) => {
  if (v === null || v === undefined || (typeof v === "string" && !v.trim())) return null;
  const f = Number(v);
  return Number.isFinite(f) ? f : null;
};

export function capacityLitres(meta = {}) {
  const kl = num(meta.capacity_kl);
  if (kl && kl > 0) return kl * 1000;
  const [L, W, H, D] = ["length_m", "width_m", "height_m", "diameter_m"].map((k) => num(meta[k]) || 0);
  let m3 = 0;
  if (meta.shape === "rectangular") m3 = L * W * H;
  else if (meta.shape === "cylindrical_vertical") m3 = Math.PI * (D / 2) ** 2 * H;
  else if (meta.shape === "cylindrical_horizontal") m3 = Math.PI * (D / 2) ** 2 * L;
  return m3 > 0 ? m3 * 1000 : null;
}

export function computeTank(meta = {}, signal) {
  const m = meta || {};
  const mode = normalizeMode(m.tx_signal_output);
  const out = {
    signal_mode: mode || String(m.tx_signal_output), signal: num(signal), status: "CONFIG ERROR",
    config_error: null, unit: null, level_pct: null, depth_m: null, litres: null, capacity_l: null,
    alarms: { low: false, very_low: false, high: false }, range_note: null,
  };
  if (!mode) {
    out.config_error = `Signal output '${m.tx_signal_output}' can't be scaled — choose 4–20 mA or a voltage mode`;
    return out;
  }
  const [dMin, dMax, dFlo, dFhi, unit] = SIGNAL_MODES[mode];
  const pick = (k, d) => (num(m[k]) === null ? d : num(m[k]));
  const smin = pick("signal_min", dMin), smax = pick("signal_max", dMax);
  const flo = pick("fault_low", dFlo), fhi = pick("fault_high", dFhi);
  Object.assign(out, { unit, signal_min: smin, signal_max: smax, fault_low: flo, fault_high: fhi });
  const R = num(m.tx_range_m), H = num(m.height_m), O = num(m.offset_m) || 0;
  const cap = capacityLitres(m);
  out.capacity_l = cap;
  const low = pick("level_low_pct", DEFAULT_LOW_PCT), vlow = pick("level_low_low_pct", DEFAULT_VERY_LOW_PCT);
  const high = num(m.level_high_pct);
  out.thresholds = { low, very_low: vlow, high };

  let err = null;
  if (H === null || H <= 0) err = "Tank height must be greater than 0";
  else if (R === null || R <= 0) err = "Transmitter range must be greater than 0";
  else if (smax <= smin) err = "Signal max must be greater than signal min";
  else if (flo > smin || fhi < smax) err = "Fault limits must lie outside signal min–max";
  else if (!(vlow >= 0 && vlow < low) || (high !== null && !(low < high && high <= 100))) {
    err = "Alarm levels must satisfy 0 ≤ low-low < low < high ≤ 100";
  }
  if (err) { out.config_error = err; return out; }

  if (Math.abs(R - H) >= 1e-4) {
    out.range_note = R > H
      ? `Range ${R} m exceeds tank height ${H} m — reads 100% from ${Math.round((H / R) * 100)}% of the signal span`
      : `Range ${R} m is shorter than tank height ${H} m — cannot read above ${Math.round((R / H) * 100)}%`;
  }
  const s = out.signal;
  if (s === null) { out.status = "NO SIGNAL"; return out; }
  if (s < flo || s > fhi) { out.status = "SENSOR FAULT"; return out; }
  out.status = s < smin ? "UNDER-RANGE" : s > smax ? "OVER-RANGE" : "OK";
  const used = Math.min(smax, Math.max(smin, s));
  const depth = Math.min(H, Math.max(0, ((used - smin) / (smax - smin)) * R + O));
  const pct = (depth / H) * 100;
  Object.assign(out, {
    depth_m: Math.round(depth * 1e4) / 1e4, level_pct: Math.round(pct * 100) / 100,
    litres: cap ? Math.round((cap * pct) / 100) : null,
    alarms: { low: pct <= low, very_low: pct <= vlow, high: high !== null && pct >= high },
  });
  return out;
}

// Level % for one raw reading, or null when it isn't a valid level (fault / config error) — for
// trend charts, where a fault must show as a gap, never as an empty tank.
export function tankLevelPct(meta, signal) {
  const t = computeTank(meta, signal);
  return VALID_STATUSES.has(t.status) ? t.level_pct : null;
}

// The tank result for a live value: the backend's when present, else computed here.
export function liveTank(asset, live) {
  if (!live) return null;
  return live.tank || computeTank(asset?.meta, live.value);
}
